import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from './Header';
import { getTranslation, Language } from '../translations';

interface EmptyCartPremiumProps {
  onBack: () => void;
}

const bounceSlow = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Wrapper = styled.div`
  max-width: 480px;
  margin: 0 auto;
  padding: 64px 16px;
  text-align: center;
  animation: ${fadeIn} 0.5s ease-out both;

  @media (max-width: 480px) {
    padding: 40px 0;
  }
`;

const IconBox = styled.div`
  width: 110px;
  height: 110px;
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 24px;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  animation: ${bounceSlow} 4s ease-in-out infinite;

  svg {
    width: 56%;
    height: 56%;
  }

  @media (max-width: 480px) {
    width: 90px;
    height: 90px;
    border-radius: 20px;
    margin-bottom: 16px;
  }
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 14px;
  letter-spacing: -0.02em;

  .dark & { color: #f1f5f9; }

  @media (max-width: 480px) {
    font-size: 1.35rem;
    margin-bottom: 10px;
  }
`;

const Description = styled.p`
  font-size: 0.9375rem;
  color: #6b7280;
  line-height: 1.55;
  max-width: 420px;
  margin: 0 auto 22px;

  .dark & { color: #94a3b8; }

  @media (max-width: 480px) {
    font-size: 0.8125rem;
    margin-bottom: 18px;
  }
`;

const CTAButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 9999px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border: none;
  color: #ffffff;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  transition: all 0.2s ease;
  margin-bottom: 20px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(59, 130, 246, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 480px) {
    padding: 10px 20px;
    font-size: 0.8125rem;
  }
`;

const SuggestionCard = styled.div`
  margin-top: 24px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  background: #f8fafc;
  text-align: left;
  position: relative;
  transition: all 0.15s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    transform: scale(1.02);
  }

  .dark & {
    background: #1a1f2e;
    border-color: #2e3441;
  }

  @media (max-width: 480px) {
    padding: 12px;
    border-radius: 10px;
    margin-top: 18px;
  }
`;

const SuggestionInner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 480px) {
    gap: 10px;
  }
`;

const SuggestionImage = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
  }
`;

const SuggestionText = styled.div`
  flex: 1;

  p {
    margin: 0;
    font-size: 0.875rem;
  }

  p:first-child {
    font-weight: 600;
    color: #111827;
    .dark & { color: #f1f5f9; }
  }

  p:last-child {
    color: #6b7280;
    .dark & { color: #94a3b8; }
  }

  @media (max-width: 480px) {
    p { font-size: 0.8125rem; }
  }
`;

const SuggestionLink = styled.a`
  font-size: 0.875rem;
  font-weight: 600;
  color: #2563eb;
  text-decoration: underline;
  white-space: nowrap;

  .dark & { color: #60a5fa; }

  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const NewBadge = styled.span`
  position: absolute;
  top: 6px;
  right: 10px;
  font-size: 0.6875rem;
  background: #fef9c3;
  color: #92400e;
  padding: 2px 6px;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  font-weight: 600;
`;

const Hint = styled.p`
  font-size: 0.8125rem;
  color: #9ca3af;
  margin-top: 16px;

  .dark & { color: #64748b; }

  @media (max-width: 480px) {
    font-size: 0.75rem;
    margin-top: 12px;
  }
`;

const EmptyCartPremium: React.FC<EmptyCartPremiumProps> = ({ onBack }) => {
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(language as Language, key);

  return (
    <Wrapper>
      <IconBox>
        <svg viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <rect x="7" y="2" width="10" height="20" rx="2"/>
          <circle cx="12" cy="18" r="1" />
        </svg>
      </IconBox>
      <Title>{t('noDeviceSelected')}</Title>
      <Description>{t('discoverValue')}</Description>
      <CTAButton onClick={onBack}>
        <span>{t('checkDeviceValue')}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12h14" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
      </CTAButton>

      <SuggestionCard>
        <SuggestionInner>
          <SuggestionImage src="/devices/iphone-13-pro.png" alt="iPhone 13 Pro" />
          <SuggestionText>
            <p>{t('mostSoldThisWeek')} iPhone 13 Pro</p>
            <p>{t('receiveUpTo')} €415</p>
          </SuggestionText>
          <SuggestionLink href="/smartphones">
            {t('discoverValueArrow')}
          </SuggestionLink>
          <NewBadge>{t('newBadge')}</NewBadge>
        </SuggestionInner>
      </SuggestionCard>

      <Hint>{t('stillDoubting')}</Hint>
    </Wrapper>
  );
};

export default EmptyCartPremium;
